import * as React from 'react';

import { R, useForm, useTranslation, useLoading, Message, useMessage, useSearch } from '@components';
import { IRequirement, IRequirementType, IRequirementValidationData, IRequirementTypeValidationHolder, RequirementValidationType } from '@models/requirements';
import { IOption, IUserIdentity } from '@models';
import dateUtils from '@utils/date-utils';

type AcceptT = (requirement: IRequirement, data: IRequirementValidationData) => Promise<IOption<boolean>>;
type RequirementCanBeValidatedF = (workId: number, requirementId: number) => Promise<boolean>;

export interface IProps {
    loading?: boolean;
    requestAccept: AcceptT;
    requestClose: Function;
    requirement: IRequirement;
    requirementType: IRequirementType;
    workId: number;
    requirementCanBeValidated: RequirementCanBeValidatedF;
    user: IUserIdentity;
    respectExpirationDate?: boolean;
    getJob: Function;
}

export function AcceptRequirement(props: IProps) {
    const { t } = useTranslation();
    const loading = useLoading(true);
    const messages = useMessage();
    const [canBeValidated, setCanBeValidated] = React.useState(false);
    const [canSetExpiration, setCanSetExpiration] = React.useState<boolean>(false);
    const workerIds = props.user.workerIds ?? [];
    const filters = {
        user_id: props.user.id,
        user_id2: props.user.id,
        user_id3: props.user.id,
        worker_id: workerIds.length > 0 ? workerIds[0] : 0,
        worker_id2: workerIds.length > 0 ? workerIds[0] : 0,
        worker_id3: workerIds.length > 0 ? workerIds[0] : 0,
        worker_id4: workerIds.length > 0 ? workerIds[0] : 0,
        worker_id5: workerIds.length > 0 ? workerIds[0] : 0,
        worker_id6: workerIds.length > 0 ? workerIds[0] : 0,
        requirement_type_id: props.requirement.requirementTypeId,
        requirement_id: props.requirement.id
    }

    const validationHolderSearch = useSearch<IRequirementTypeValidationHolder>({
        workId: props.requirement.workId,
        search: 'jobRequirements/validationHolder',
        filters: filters
    });

    const initialize = loading.wrap(async () => {
        const canBeValidated = await props.requirementCanBeValidated(
            props.requirement.workId,
            props.requirement.id);
        setCanBeValidated(canBeValidated || props.user.roles.includes('gestor'));
        const validationHolder = await validationHolderSearch.doSearch();
        setCanSetExpiration(validationHolder?.[0]?.canSetExpiration ?? true);
    });

    React.useEffect(() => {
        initialize();
    }, []);

    const form = useForm<IRequirementValidationData>({
        initialValues: {
            remarks: '',
            expirationThreshold: (props.requirementType?.expirationThreshold),
            expirationDate: (props.respectExpirationDate && props.requirement.expirationDate ) ? dateUtils.toUtc(props.requirement.expirationDate ) : undefined,
        },
    });
    const doAccept = () => {

        if(props.requirementType.expirationRequiredWhenValidated && !form.values.expirationDate && canSetExpiration) {
            messages.setError(t('requirements.validation.expiration-date-mandatory'));
            return;
        }
        if (props.requirementType.expirationMaxMonths && form.values.expirationDate) {
            const maxDate = dateUtils.isEndDateWithinMaxDuration(
                new Date(),
                form.values.expirationDate,
                props.requirementType.expirationMaxMonths);
            if (!maxDate) {
                messages.setError(t('requirements.validation.expiration-date-max-months', { maxMonths: props.requirementType.expirationMaxMonths }));
                return;
            }
        }

        if (!canSetExpiration && props.requirementType.validationTypeId == RequirementValidationType.REQUIRE_ALL ) {
            form.values.expirationDate = props.requirement.expirationDate;
        }

        props.requestAccept(props.requirement, form.values);
    }

    const suggestions = (props.requirementType?.suggestions ?? []).map(s => s.name);

    const deleteDate = () => {
        form.setFieldValue('expirationDate', undefined);
    }

    return <div>
        <div className='form-1 l200 sm pd'>
            <R label={t('requirements.observations')}>
                {form.autoCompleteTextArea(
                    'remarks',
                    {suggestions, style: {minWidth: '400px', minHeight: '150px'}})}
            </R>
            <div className='ValidateDate'>
            {canSetExpiration && <R label={t('Expiration date')}>
                {form.calendar('expirationDate')}
                <i className='pi pi-trash pointer' title={t('delete.calendar')}
                        onClick={deleteDate}/>
            </R>}
            </div>
            <R label={t('Tolerance')}>
                {form.input('expirationThreshold', {type: 'number'})}
            </R>
        </div>

        <div style={{ display: 'grid' }}>
            {messages.render()}
        </div>

        {!canBeValidated &&
            <div className='sm pd text-sm r'>
                <Message
                    className='e'
                    severity={'error'}
                    text={t('requirement.canNotBeValidated')} />
            </div>}
        <div className='footer r r-end'>
            <button onClick={() => props.requestClose()}>{t('Cancel')}</button>
            {canBeValidated && <button disabled={props.loading} onClick={() => doAccept()} className='success'>{t('Validate')}</button>}
        </div>
    </div>
}
